/**
 * Route Guard Components for React Router 6
 *
 * These components replace the onEnter hooks from React Router 3.
 * They wrap route elements and handle authentication/authorization redirects.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import {
  isLoggedInSelector,
  canGiftSubscriptionSelector,
  isLifetimeSelector
} from 'reduxModules/auth/selectors'
import {
  readyToReviewOrderSelector,
  isLifetimePlanSelector
} from 'reduxModules/checkout/selectors'
import { clientTokenSelector } from 'reduxModules/forms/paymentMethod/selectors'
import {
  isSubscribedReselector,
  isLibrarySubscriberSelector
} from 'reduxModules/users/selectors'

/**
 * Requires user to be authenticated
 * Redirects to /login if not authenticated
 */
export const RequireAuth = ({ children }) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (!isLoggedIn) {
    // Keep the query string so deep links (e.g. /certificates?course=<slug>)
    // survive the login round-trip.
    return (
      <Navigate
        to="/login"
        state={{ next: location.pathname + location.search }}
        replace
      />
    )
  }

  return children
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT be authenticated
 * Redirects to / if authenticated
 */
export const RequireNoAuth = ({ children }) => {
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (isLoggedIn) {
    return <Navigate to="/" state={{ next: location.pathname }} replace />
  }

  return children
}

RequireNoAuth.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to have an active subscription
 * Redirects to /subscriptions/plans if not subscribed
 */
export const RequireSubscribed = ({ children }) => {
  const { isSubscribed } = useSelector(isSubscribedReselector)
  const isLoggedIn = useSelector(isLoggedInSelector)
  const location = useLocation()

  if (!isSubscribed || !isLoggedIn) {
    return (
      <Navigate
        to="/subscriptions/plans"
        state={{ next: location.pathname }}
        replace
      />
    )
  }

  return children
}

RequireSubscribed.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT have an active subscription
 * Redirects to /change_subscription if subscribed
 */
export const RequireNotSubscribed = ({ children }) => {
  const { isSubscribed } = useSelector(isSubscribedReselector)
  const location = useLocation()

  if (isSubscribed) {
    const codename = new URLSearchParams(location.search).get('codename')
    const to = codename
      ? `/change_subscription?codename=${encodeURIComponent(codename)}`
      : '/change_subscription'
    return (
      <Navigate
        to={to}
        state={{ next: location.pathname }}
        replace
      />
    )
  }

  return children
}

RequireNotSubscribed.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires order to be ready for review
 * Redirects to /checkout if order not ready
 */
export const RequireOrderReady = ({ children }) => {
  const orderReady = useSelector(readyToReviewOrderSelector)
  const hasClientToken = useSelector(clientTokenSelector) !== ''
  const location = useLocation()

  if (!orderReady || !hasClientToken) {
    return (
      <Navigate to="/checkout" state={{ next: location.pathname }} replace />
    )
  }

  return children
}

RequireOrderReady.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT be a library subscriber.
 * Library subscribers are allowed through when they're checking out the Lifetime plan
 * (lifetime supersedes any current entitlement).
 */
export const RequireNotLibrarySubscriber = ({ children }) => {
  const isLibrarySubscriber = useSelector(isLibrarySubscriberSelector)
  const isLifetimePlan = useSelector(isLifetimePlanSelector)
  const location = useLocation()

  if (isLibrarySubscriber && !isLifetimePlan) {
    return <Navigate to="/" state={{ next: location.pathname }} replace />
  }

  return children
}

RequireNotLibrarySubscriber.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to NOT already own a Lifetime subscription.
 * Pre-empts the checkout page for already-lifetime users (also serves as a
 * belt-and-suspenders companion to the dispatcher's 409 fallback).
 */
export const RequireNotLifetime = ({ children }) => {
  const isLifetime = useSelector(isLifetimeSelector)
  const location = useLocation()

  if (isLifetime) {
    return (
      <Navigate
        to="/account?lifetimeOwned=1"
        state={{ next: location.pathname }}
        replace
      />
    )
  }

  return children
}

RequireNotLifetime.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Requires user to have gift subscription capability
 * Redirects to /account if user cannot gift subscription
 */
export const RequireGiftSubscription = ({ children }) => {
  const canGiftSubscription = useSelector(canGiftSubscriptionSelector)
  const location = useLocation()

  if (!canGiftSubscription) {
    return (
      <Navigate to="/account" state={{ next: location.pathname }} replace />
    )
  }

  return children
}

RequireGiftSubscription.propTypes = {
  children: PropTypes.node.isRequired
}

export default {
  RequireAuth,
  RequireNoAuth,
  RequireSubscribed,
  RequireNotSubscribed,
  RequireOrderReady,
  RequireNotLibrarySubscriber,
  RequireNotLifetime,
  RequireGiftSubscription
}
